import { useState, useEffect, useCallback, useRef } from "react";
import {
  CheckCircle, AlertCircle, Circle,
  Loader2, RefreshCw, Eye,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import MediaPicker from "./MediaPicker";
import { AdminSelect } from "./AdminSelect";
import AutoGeneratePanel from "./AutoGeneratePanel";
import PresentationSlotCard from "./PresentationSlotCard";

const slotPillCls = (hasAsset, required) => {
  if (hasAsset) return "bg-green-500/15 text-green-400";
  if (required) return "bg-red-500/10 text-red-400/60";
  return "bg-white/5 text-white/20";
};

/**
 * PresentationBuilder
 * Props:
 *   productId  — the product's id string (required)
 *   productType — "canvas" | "wall_mural" | "" (used to pre-filter packs)
 *   onSave(result) — optional callback after successful save
 */
export default function PresentationBuilder({ productId, productType = "", mainImageUrl = "", onSave }) {
  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState("");
  const [presentation, setPresentation] = useState(null);
  const [slotData, setSlotData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pickerSlot, setPickerSlot] = useState(null);
  const [saveResult, setSaveResult] = useState(null);
  const [artworkUrl, setArtworkUrl] = useState(mainImageUrl || "");

  useEffect(() => {
    if (mainImageUrl && !artworkUrl) setArtworkUrl(mainImageUrl);
  }, [mainImageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizedType = productType
    ? productType.toLowerCase().replace(/[^a-z]/g, "_").replace(/prints?/, "").replace(/_+/g, "_").replace(/^_|_$/g, "")
    : "";

  const inferredType = (() => {
    if (!normalizedType) return "";
    if (normalizedType.includes("canvas")) return "canvas";
    if (normalizedType.includes("mural")) return "wall_mural";
    return "";
  })();

  const fetchPacks = useCallback(async () => {
    try {
      const query = inferredType ? `?product_type=${inferredType}` : "";
      const res = await adminApi.get(`/api/admin/templates/packs${query}`);
      const packList = res.data.packs || [];
      setPacks(packList);
      const defaultPack = packList.find((p) => p.default_pack && p.active);
      if (defaultPack && !selectedPackId) {
        setSelectedPackId(defaultPack.id);
      } else if (packList.length > 0 && !selectedPackId) {
        setSelectedPackId(packList[0].id);
      }
    } catch (err) {
      console.error("[PresentationBuilder] Failed to load template packs:", err);
    }
  }, [inferredType, selectedPackId]);

  const fetchPresentation = useCallback(async () => {
    if (!productId) { setLoading(false); return; }
    try {
      const res = await adminApi.get(`/api/admin/templates/presentations/${productId}`);
      const pres = res.data.presentation;
      if (pres) {
        setPresentation(pres);
        setSelectedPackId(pres.template_pack_id || "");
        setSlotData(pres.slots || {});
      }
    } catch (err) {
      console.error("[PresentationBuilder] Failed to load presentation:", err);
    }
  }, [productId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPacks(), fetchPresentation()]).then(() => setLoading(false));
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPack = packs.find((p) => p.id === selectedPackId);
  const sortedSlots = selectedPack
    ? [...(selectedPack.slots || [])].sort((a, b) => a.order - b.order)
    : [];

  const requiredSlots = sortedSlots.filter((s) => s.required);
  const filledRequired = requiredSlots.filter((s) => slotData[s.key]?.asset_url?.trim());

  const updateSlot = (key, field, value) => {
    setSlotData((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const handleFileUpload = async (slotKey, file) => {
    updateSlot(slotKey, "_uploading", true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", "presentation");
      fd.append("group", `${inferredType || "product"}-presentation`);
      fd.append("status", "published");
      const res = await adminApi.post("/api/admin/media/upload", fd);
      updateSlot(slotKey, "asset_url", res.data.file_url);
      updateSlot(slotKey, "asset_id", res.data.id);
      updateSlot(slotKey, "source_type", "manual_upload");
    } catch (err) {
      console.error("[PresentationBuilder] File upload failed for slot:", slotKey, err);
      updateSlot(slotKey, "_uploadError", err.message || "Upload failed");
    }
    updateSlot(slotKey, "_uploading", false);
  };

  const handlePickerSelect = (url, assetId) => {
    if (!pickerSlot) return;
    updateSlot(pickerSlot, "asset_url", url);
    updateSlot(pickerSlot, "asset_id", assetId || "");
    updateSlot(pickerSlot, "source_type", "manual_upload");
    setPickerSlot(null);
  };

  const handleSave = async () => {
    if (!selectedPackId || !productId) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const cleanSlots = {};
      for (const [k, v] of Object.entries(slotData)) {
        const { _uploading, _uploadError, ...rest } = v || {};
        cleanSlots[k] = rest;
      }
      const res = await adminApi.put(`/api/admin/templates/presentations/${productId}`, {
        template_pack_id: selectedPackId,
        product_type: inferredType || productType,
        slots: cleanSlots,
      });
      setSaveResult({ success: true, completed: res.data.completed });
      setPresentation((prev) => ({ ...(prev || {}), completed: res.data.completed }));
      if (onSave) onSave(res.data);
    } catch (err) {
      setSaveResult({ success: false, message: err.response?.data?.detail || "Save failed" });
    }
    setSaving(false);
  };

  const handleSlotsGenerated = (generated) => {
    setSlotData((prev) => {
      const updated = { ...prev };
      for (const [key, data] of Object.entries(generated)) {
        updated[key] = { ...(updated[key] || {}), ...data };
      }
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="border border-white/[0.06] bg-[#060606] p-6 text-center">
        <Loader2 size={16} className="animate-spin text-white/30 mx-auto mb-2" />
        <p className="text-xs text-white/30">Loading presentation...</p>
      </div>
    );
  }

  if (packs.length === 0) {
    return (
      <div className="border border-white/[0.06] bg-[#060606] p-6 text-center" data-testid="presentation-no-packs">
        <AlertCircle size={16} className="text-yellow-400 mx-auto mb-2" />
        <p className="text-xs text-yellow-400 mb-1">No template packs available</p>
        <p className="text-[10px] text-white/30">Go to the Templates tab and click "Seed Default Packs" to get started.</p>
      </div>
    );
  }

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="presentation-builder">
      {/* Header */}
      <div className="p-4 border-b border-white/[0.06] flex items-center gap-3 flex-wrap">
        <Eye size={15} className="text-[#CBA052]" />
        <span className="text-xs font-bold text-white">Presentation Builder</span>
        {presentation?.completed && (
          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-green-500/15 text-green-400 border-green-500/20">Complete</span>
        )}
        {presentation && !presentation.completed && (
          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-yellow-500/15 text-yellow-400 border-yellow-500/20">Incomplete</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] text-white/30">
            {filledRequired.length}/{requiredSlots.length} required
          </span>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {/* Pack Selector */}
        <div>
          <label className="text-[10px] text-white/50 uppercase tracking-wider block mb-1.5">Template Pack</label>
          <AdminSelect value={selectedPackId} onChange={(e) => setSelectedPackId(e.target.value)} size="md" data-testid="pack-selector">
            <option value="">— Select a template pack —</option>
            {packs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.default_pack ? " (Default)" : ""}{!p.active ? " (Inactive)" : ""}
              </option>
            ))}
          </AdminSelect>
          {selectedPack && <p className="text-[10px] text-white/30 mt-1">{selectedPack.description}</p>}
        </div>

        {/* Auto-Generate Panel */}
        {selectedPack && (
          <AutoGeneratePanel
            sortedSlots={sortedSlots}
            artworkUrl={artworkUrl}
            setArtworkUrl={setArtworkUrl}
            selectedPackId={selectedPackId}
            productId={productId}
            inferredType={inferredType}
            onSlotsGenerated={handleSlotsGenerated}
          />
        )}

        {/* Slots */}
        {selectedPack && sortedSlots.length > 0 && (
          <div className="space-y-3" data-testid="presentation-slots">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] text-white/40 uppercase tracking-wider">Gallery Slots</span>
              <span className="text-[9px] text-white/20">— ordered by template pack</span>
            </div>
            {sortedSlots.map((slot) => (
              <PresentationSlotCard
                key={slot.key}
                slot={slot}
                data={slotData[slot.key]}
                onUpdateSlot={updateSlot}
                onPickFromLibrary={setPickerSlot}
                onUpload={handleFileUpload}
              />
            ))}
          </div>
        )}

        {/* Gallery order preview */}
        {sortedSlots.length > 0 && (
          <div className="border border-white/[0.04] rounded p-3 bg-white/[0.01]">
            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Gallery Order (auto from pack)</p>
            <div className="flex items-center gap-1 flex-wrap">
              {sortedSlots.map((slot, idx) => {
                const hasAsset = !!(slotData[slot.key]?.asset_url || "").trim();
                return (
                  <span
                    key={slot.key}
                    className={`text-[9px] px-2 py-0.5 rounded ${slotPillCls(hasAsset, slot.required)}`}
                  >
                    {idx + 1}. {slot.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Save */}
        {selectedPack && (
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !selectedPackId}
              className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-4 py-2 rounded hover:opacity-90 disabled:opacity-50 transition-opacity"
              data-testid="presentation-save-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {saving ? "Saving..." : "Save Presentation"}
            </button>
            {saveResult && (
              <span className={`text-xs ${saveResult.success ? "text-green-400" : "text-red-400"}`}>
                {saveResult.success
                  ? saveResult.completed ? "Saved — presentation complete" : "Saved — required slots still missing"
                  : saveResult.message}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Media Picker Modal */}
      {pickerSlot && (
        <MediaPicker
          slotLabel={sortedSlots.find((s) => s.key === pickerSlot)?.label || pickerSlot}
          onSelect={handlePickerSelect}
          onClose={() => setPickerSlot(null)}
        />
      )}
    </div>
  );
}
